import { KNOWN_AGENT_KINDS, agentKindLabel, agentKindOf, approvalBlockFor, approvalsForDerive, isTestRun, machineLiveness } from '@orgus/protocol';
import type { ApprovalsState, Session } from '@orgus/protocol';
import { CircleStop, Link2, MessageCircleQuestion, Plus, Search, SearchX, SlidersHorizontal, SquareTerminal, TriangleAlert } from '@orgus/ui/icons';
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import {
  Banner,
  BulkBar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  GroupChevron,
  Kbd,
  LiveNumber,
  Panel,
  Pressable,
  Segmented,
  Skeleton,
  StatStrip,
  StatTile,
  WorkStateIcon,
  cn,
  formatDuration,
  formatInt,
  isTypingTarget,
  plural,
  toast,
  useNow,
  useSelection,
} from '@orgus/ui';
import type { FilterChip, SegmentedOption } from '@orgus/ui';
import { useOrgus, useAllSessions, useMachines, useProjects } from '../../lib/store.ts';
import { openNewSession } from '../new-session/store.ts';
import { agentKindsIn, filterSessions, isFiltered, matchesStatus, useSessionFilters } from './filters.ts';
import type { ProjectGroup, StatusFilter } from './filters.ts';
import { blockedSince, groupByState, oldestWait, runningAgents, startsCollapsed } from './groups.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import type { SessionGroupKey, SessionStateGroup } from './groups.ts';
import { COL, SessionRow } from './SessionRow.tsx';
import type { SessionColumns } from './SessionRow.tsx';
import { SessionsGlance } from './SessionsGlance.tsx';
import { useSessionParam } from './useSessionParam.ts';
import { VirtualGroups } from './VirtualGroups.tsx';
import { FailureFoldRow } from './FailureFoldRow.tsx';
import { VIRTUAL_MIN_ROWS } from './virtual.ts';
import { sessionPredicate } from '../ask/evaluate.ts';
import { ViewBar } from '../views/ViewBar.tsx';
import { useAskData, useWhere } from '../views/useWhere.ts';
import { stopSessionsAction } from '../act/catalogue.ts';
import { performAct } from '../act/run.ts';
import { usePublishSelection } from '../act/selection.ts';

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  live: 'Live',
  waiting: 'Needs you',
  working: 'Working',
  idle: 'Idle',
  ended: 'Ended',
  succeeded: 'Succeeded',
  failed: 'Failed',
  tests: 'Tests',
};
const STATUS_ORDER: StatusFilter[] = ['all', 'live', 'waiting', 'working', 'idle', 'ended', 'succeeded', 'failed', 'tests'];

export function SessionsSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading sessions">
      <StatStrip label="Loading session counts" className="lg:grid-cols-4">
        {['Needs you', 'Live sessions', 'Subagents running', 'Spend today'].map((l) => (
          <StatTile key={l} label={l} value={0} loading />
        ))}
      </StatStrip>
      <Panel flush>
        <div className="flex h-filter-bar items-center gap-2 px-4 pt-2">
          <Skeleton className="h-control-sm w-56" />
          <Skeleton className="h-control-sm w-96" />
        </div>
        <div className="flex flex-col gap-1 p-2">
          {Array.from({ length: 9 }, (_, i) => (
            <div key={i} className="flex h-row items-center gap-3 px-row-x">
              <Skeleton className="size-4 rounded-pill" />
              <Skeleton className="h-3" style={{ width: `${32 + ((i * 17) % 28)}%` }} />
              <Skeleton className="ml-auto h-3 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/** Machine, agent and project in one menu; each active choice shows as a chip in the view bar. */
function ScopeMenu({ agents }: { agents: { kind: string; count: number; present: boolean }[] }) {
  const [f, set] = useSessionFilters();
  const machines = useMachines();
  const projects = useProjects();
  // Offered when the fleet runs more than one agent, whatever the search: a filter that
  // disappears as you type moves every control after it.
  const showAgents = agents.filter((a) => a.present).length > 1 || !!f.agentKind;
  const active = [f.machineId, f.agentKind, f.projectId].filter(Boolean).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" icon={<SlidersHorizontal />} aria-label={active ? `Scope, ${formatInt(active)} set` : 'Scope'}>
          Scope
          {active > 0 && <span className="text-fg-subtle num">{formatInt(active)}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span className="flex-1">Project</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={f.projectId} onValueChange={(projectId) => set({ projectId })}>
              <DropdownMenuRadioItem value="">All projects</DropdownMenuRadioItem>
              {projects.map((p) => (
                <DropdownMenuRadioItem key={p.id} value={p.id}>
                  {p.name}
                </DropdownMenuRadioItem>
              ))}
              <DropdownMenuRadioItem value="none">No project</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {machines.length > 1 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="flex-1">Machine</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={f.machineId} onValueChange={(machineId) => set({ machineId })}>
                <DropdownMenuRadioItem value="">All machines</DropdownMenuRadioItem>
                {machines.map((m) => (
                  <DropdownMenuRadioItem key={m.id} value={m.id}>
                    {m.online ? m.name : `${m.name} (offline)`}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {showAgents && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="flex-1">Agent</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={f.agentKind ?? ''} onValueChange={(agentKind) => set({ agentKind })}>
                <DropdownMenuRadioItem value="">All agents</DropdownMenuRadioItem>
                {agents.map(({ kind, count }) => (
                  <DropdownMenuRadioItem key={kind} value={kind}>
                    <span className="flex-1">{agentKindLabel(kind)}</span>
                    <span className="pl-4 text-caption text-fg-subtle num">{formatInt(count)}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Toolbar({ counts, agents, views }: { counts: Record<StatusFilter, number>; agents: { kind: string; count: number; present: boolean }[]; views: (chips: FilterChip[], clear: () => void, trailing: ReactNode) => ReactNode }) {
  const [f, set] = useSessionFilters();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const searchRef = useRef<HTMLInputElement>(null);
  // The box owns its text; the address follows shortly after, so fast typing never drops keys.
  const [q, setQ] = useState(f.q);
  const sent = useRef(f.q);
  useEffect(() => {
    if (f.q !== sent.current) {
      sent.current = f.q;
      setQ(f.q);
    }
  }, [f.q]);
  useEffect(() => {
    if (q === sent.current) return;
    const t = setTimeout(() => {
      sent.current = q;
      set({ q });
    }, 120);
    return () => clearTimeout(t);
  }, [q, set]);

  // '/' focuses search, the way every list in the product does.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Tests shows only while there are test, probe or experiment runs (or it is the filter).
  const statusOptions: SegmentedOption<StatusFilter>[] = STATUS_ORDER.filter((s) => s !== 'tests' || counts.tests > 0 || f.status === 'tests').map((s) => ({ value: s, label: STATUS_LABEL[s], count: counts[s] }));
  // The scope the menu set, as chips beside the saved views (each one removable).
  const chips: FilterChip[] = [];
  if (f.projectId) chips.push({ id: 'project', field: 'Project', value: f.projectId === 'none' ? 'No project' : (projects[f.projectId]?.name ?? 'Unknown project'), onRemove: () => set({ projectId: '' }) });
  if (f.machineId) chips.push({ id: 'machine', field: 'Machine', value: machines[f.machineId]?.name ?? 'Unknown machine', onRemove: () => set({ machineId: '' }) });
  if (f.agentKind) chips.push({ id: 'agent', field: 'Agent', value: agentKindLabel(f.agentKind), onRemove: () => set({ agentKind: '' }) });

  return (
    <div className="flex min-h-filter-bar flex-wrap items-center gap-2 px-3 pt-3 pb-1" data-chip-group="">
      <Input
        ref={searchRef}
        size="sm"
        icon={<Search />}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && q) {
            e.preventDefault();
            setQ('');
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            document.querySelector<HTMLElement>('[data-session-row]')?.focus();
          }
        }}
        placeholder="Search sessions"
        aria-label="Search sessions"
        trailing={!q ? <Kbd keys={['/']} /> : undefined}
        wrapperClassName="w-full sm:w-56"
      />
      <div className="max-w-full overflow-x-auto">
        <Segmented label="Status" value={f.status} onChange={(status) => set({ status })} options={statusOptions} />
      </div>
      <div className="flex min-w-0 flex-1 items-center">{views(chips, () => set({ machineId: '', projectId: '', agentKind: '' }), <ScopeMenu agents={agents} />)}</div>
    </div>
  );
}

/** Column titles over the numbers. What every row shares is said here, once. */
function ColumnHeader({ shared }: { shared: string }) {
  const head = 'shrink-0 truncate text-right text-caption text-fg-subtle';
  return (
    <div className="hidden h-8 items-center gap-3 px-row-x md:flex" aria-hidden>
      <span className="w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-caption text-fg-subtle">{shared}</span>
      <span className={cn(COL.agents, head)}>Agents</span>
      <span className={cn(COL.tokens, head)}>Tokens</span>
      <span className={cn(COL.cost, head)}>Cost</span>
      <span className={cn(COL.active, head)}>Active</span>
    </div>
  );
}

/** A quiet banner when machines are not reporting: their sessions show what they last sent. */
function OfflineBanner({ sessions }: { sessions: Session[] }) {
  const machines = useOrgus((s) => s.machines);
  const now = useNow(10_000);
  const ids = [...new Set(sessions.filter((s) => s.status !== 'ended').map((s) => s.machineId))];
  const down = ids.filter((id) => !machines[id] || machineLiveness(machines[id], now) !== 'online');
  if (!down.length) return null;
  const names = down.map((id) => machines[id]?.name ?? 'An unknown machine');
  return (
    <Banner tone="attention" icon={<TriangleAlert />} title={down.length === 1 ? `${names[0]} is not reporting` : `${plural(down.length, 'machine')} are not reporting`} className="mx-3 mb-2">
      Their live sessions show what they last sent. Check the machine is on and its agent running.
    </Banner>
  );
}

/** Arrow keys and j/k move through rows; Enter opens. */
function onListKey(e: KeyboardEvent<HTMLDivElement>) {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const down = e.key === 'ArrowDown' || e.key === 'j';
  const up = e.key === 'ArrowUp' || e.key === 'k';
  if (!down && !up) return;
  const rows = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-session-row]')];
  const i = rows.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  e.preventDefault();
  rows[Math.min(rows.length - 1, Math.max(0, i + (down ? 1 : -1)))]?.focus();
}

/** Which facts vary across the rows shown, and the shared ones said once above them. */
function columnsFor(shown: Session[], projectName: (id: string | null) => string, machineName: (id: string) => string): { cols: SessionColumns; shared: string } {
  const projects = new Set(shown.map((s) => s.projectId ?? null));
  const machines = new Set(shown.map((s) => s.machineId));
  const cols = { project: projects.size > 1, machine: machines.size > 1 };
  const parts = [projects.size === 1 ? `All in ${projectName([...projects][0]!)}` : '', machines.size === 1 ? `${projects.size === 1 ? 'on' : 'All on'} ${machineName([...machines][0]!)}` : ''].filter(Boolean);
  return { cols, shared: parts.join(' ') };
}

/**
 * One quiet fact in a group's header, only when it adds something: how long the oldest blocked
 * session has waited (from the same blocked-only reading as the glance tile; nothing under a
 * minute), how many agents run, how many ended ones a decided approval stopped. A count the header already shows is never said again.
 */
function groupMeta(g: SessionStateGroup, now: number, since: ReadonlyMap<string, number>, blocked?: ReadonlySet<string>): string | undefined {
  if (g.key === 'needs-you') {
    const wait = oldestWait(g.sessions, now, since);
    return wait ? `oldest ${formatDuration(wait)}` : undefined;
  }
  if (g.key === 'working') {
    const a = runningAgents(g.sessions);
    return a.subagents ? `${plural(a.subagents, 'subagent')} running` : undefined;
  }
  if (g.key === 'ended' && blocked?.size) {
    // How the ended ones came out, when not all simply finished (failures sit under Failed).
    const held = g.sessions.filter((s) => blocked.has(s.id)).length;
    return held ? `${formatInt(g.sessions.length - held)} finished, ${formatInt(held)} blocked` : undefined;
  }
  return undefined;
}

/** A group's icon: its work state, or the Inbox's question glyph for Asked (neutral, ADR 0056). */
function GroupIcon({ g }: { g: SessionStateGroup }) {
  return g.key === 'asked' ? <MessageCircleQuestion className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : <WorkStateIcon state={g.meta.state} />;
}

/** A state header for the windowed list (many sessions): the same look as SessionGroup, its fold lifted out. */
function VirtualStateHeader({ g, open, onToggle, meta }: { g: SessionStateGroup; open: boolean; onToggle: () => void; meta?: string }) {
  return (
    <Pressable onClick={onToggle} aria-expanded={open} className="flex h-row w-full items-center gap-2 rounded-control bg-surface pl-row-x text-left hover:bg-hover">
      <GroupChevron open={open} />
      <GroupIcon g={g} />
      <span className="truncate text-ui font-medium text-fg">{g.meta.label}</span>
      <span className="text-ui text-fg-subtle num">{formatInt(g.sessions.length)}</span>
      {meta && <span className="hidden truncate text-caption text-fg-subtle sm:inline">{meta}</span>}
    </Pressable>
  );
}

/**
 * A group of sessions in one state (the StateGroup look: icon, label, live count, one meta fact)
 * with Asked drawn with the question glyph, and rows one step in only from tablet width up: on a
 * phone the indent would take the title's room.
 */
function SessionGroup({ g, meta, defaultCollapsed, children }: { g: SessionStateGroup; meta?: string; defaultCollapsed: boolean; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const id = `state-group-sessions-${g.key}-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <section aria-labelledby={id} data-state-group={`sessions-${g.key}`} className="flex min-w-0 flex-col">
      <div className="flex h-(--group-header-height) min-w-0 items-center gap-2 pr-2">
        <Pressable onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed} className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-control pl-row-x text-left -outline-offset-2 hover:bg-hover">
          <GroupChevron open={!collapsed} />
          <GroupIcon g={g} />
          <span id={id} className="truncate text-ui font-medium text-fg">
            {g.meta.label}
          </span>
          <LiveNumber value={g.sessions.length} className="text-ui text-fg-subtle" />
          {meta != null && <span className="hidden min-w-0 truncate text-caption text-fg-subtle sm:inline">{meta}</span>}
        </Pressable>
      </div>
      {!collapsed && (
        <div data-group-body="" className="flex min-w-0 flex-col md:pl-6">
          {children}
        </div>
      )}
    </section>
  );
}

/** A panel on a phone, run edge to edge: square, with no side borders. */
const PHONE_EDGE = 'max-md:rounded-none max-md:border-x-0';

/** Every session, grouped by state (what needs you first), with the filters on the list's own top edge. */
// Memoised: the page re-renders when this reports its row order, and that must not render the list again.
export const SessionsView = memo(function SessionsView({ onOrder }: { onOrder?: (ids: string[]) => void }) {
  const [f, , clear] = useSessionFilters();
  // Unsorted: filterSessions and groupByState order the rows themselves.
  const all = useAllSessions();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const checkouts = useOrgus((s) => s.checkouts);
  const [openId, setOpenId] = useSessionParam();
  const now = useNow(30_000);

  // Each waiting session's place in Needs you (ADR 0056): asked questions group apart, old or
  // dismissed ones file under Idle.
  const needs = useNeedsYouState();
  const levels = needs.bySession;
  const since = useMemo(() => blockedSince(needs.blocked), [needs.blocked]);
  // Ended sessions a decided approval stopped (the rows' Blocked mark), for the Ended header's outcome.
  const approvals = useOrgus((s) => s.ext.approvals as ApprovalsState | undefined);
  const blockedEnded = useMemo(() => {
    const list = approvals ? (approvalsForDerive(approvals) ?? []) : [];
    if (!list.length) return undefined;
    const at = Date.now();
    return new Set(all.filter((s) => {
      const b = s.status === 'ended' ? approvalBlockFor(s, list, at) : null;
      return b != null && b.phase !== 'resuming';
    }).map((s) => s.id));
  }, [approvals, all]);
  const lookup = useMemo(() => ({ projects, machines, levels }), [projects, machines, levels]);
  // A filter in words (`?where=failed today on build-01`, from Ask or a saved view) narrows every count too.
  const where = useWhere('sessions');
  const askData = useAskData(useNow(30_000));
  const wherePred = useMemo(() => sessionPredicate(where.clauses, askData), [where.clauses, askData]);
  // Everything in scope whatever its status: the glance strip and the status counts read it, so
  // each number says what clicking it shows.
  // Test runs included: the Tests count reads them; the glance and every other status leave them out.
  const scoped = useMemo(() => filterSessions(all, f, lookup, { anyStatus: true }).filter(wherePred), [all, f, lookup, wherePred]);
  const real = useMemo(() => scoped.filter((s) => !isTestRun(s)), [scoped]);
  const counts = useMemo(() => {
    const c = {} as Record<StatusFilter, number>;
    for (const s of STATUS_ORDER) c[s] = scoped.filter((x) => matchesStatus(x, s, levels)).length;
    return c;
  }, [scoped, levels]);
  const shown = useMemo(() => scoped.filter((s) => matchesStatus(s, f.status, levels)), [scoped, f.status, levels]);
  // Every known agent is offered (a provider with no sessions shows 0), plus any other kind present.
  const agents = useMemo(() => {
    const base = filterSessions(all, { ...f, agentKind: '' }, lookup);
    const present = new Set(agentKindsIn(all));
    const kinds = [...new Set([...KNOWN_AGENT_KINDS, ...present])];
    return kinds.map((kind) => ({ kind, count: base.filter((s) => agentKindOf(s) === kind).length, present: present.has(kind) }));
  }, [all, f, lookup]);
  // Failures lead for a day, below live work; older ones are history under Ended (ADR 0059).
  const groups = useMemo(() => groupByState(shown, levels, now), [shown, levels, now]);
  const narrowed = f.status !== 'all' || !!f.q.trim() || where.clauses.length > 0;
  // The fold of each group, lifted here so the windowed list and the row order agree on it.
  const [folds, setFolds] = useState<Partial<Record<SessionGroupKey, boolean>>>({});
  const isOpen = (g: SessionStateGroup) => !(folds[g.key] ?? startsCollapsed(g.key, groups, narrowed));
  const order = useMemo(() => groups.flatMap((g) => g.sessions.map((s) => s.id)), [groups]);
  const machineName = (id: string) => machines[id]?.name ?? 'Unknown machine';
  const projectName = (id: string | null) => (id ? (projects[id]?.name ?? 'Unknown project') : 'No project');
  const { cols, shared } = useMemo(
    () => columnsFor(shown, (id) => (id ? (projects[id]?.name ?? 'an unknown project') : 'no project'), (id) => machines[id]?.name ?? 'an unknown machine'),
    [shown, projects, machines],
  );
  // One state shown: its header would only repeat the status filter (spacing-layout.md, Rows).
  const headers = groups.length > 1;

  useEffect(() => onOrder?.(order), [order, onOrder]);

  // Bulk select: a checkbox on each row (or x on a focused row), Shift for a range, Esc clears.
  const sel = useSelection(order);
  const picked = useMemo(() => shown.filter((s) => sel.selected.has(s.id)), [shown, sel.selected]);
  usePublishSelection('sessions', useMemo(() => picked.map((s) => s.id), [picked]));
  const running = picked.filter((s) => s.status !== 'ended');
  const onRowsKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'x' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const id = (document.activeElement as HTMLElement | null)?.dataset.sessionRow;
      if (id) {
        e.preventDefault();
        sel.toggle(id, e.shiftKey);
      }
      return;
    }
    onListKey(e);
  };

  if (!all.length) {
    return (
      <Panel>
        <EmptyState
          size="page"
          icon={<SquareTerminal />}
          title="No sessions yet"
          description="Sessions on every connected machine show up here as they start."
          action={
            <Button variant="primary" icon={<Plus />} onClick={() => openNewSession()}>
              New session
            </Button>
          }
        />
      </Panel>
    );
  }

  const row = (s: Session, style?: CSSProperties, className?: string) => {
    const c = s.checkoutId ? checkouts[s.checkoutId] : undefined;
    const def = s.projectId ? projects[s.projectId]?.defaultBranch : undefined;
    return (
      <SessionRow
        key={s.id}
        s={s}
        style={style}
        className={className}
        branch={c?.branch && c.branch !== def ? c.branch : null}
        machineName={machineName(s.machineId)}
        projectName={projectName(s.projectId ?? null)}
        cols={cols}
        selected={openId === s.id}
        onOpen={() => setOpenId(s.id)}
        checked={sel.isSelected(s.id)}
        anyChecked={sel.count > 0}
        onCheck={sel.toggle}
      />
    );
  };

  // The windowed list takes project-shaped groups: one per state, its key in projectId.
  const windowed: ProjectGroup[] = groups.map((g) => ({ projectId: g.key, sessions: isOpen(g) ? g.sessions : [], machines: [], count: g.sessions.length, lastActivity: 0 }));
  const byKey = new Map(groups.map((g) => [g.key as string, g]));

  // On a phone the strip and the list run edge to edge (past the page's gutter, square, no side
  // borders), so a title gets the room the gutter and the panel's edges took.
  return (
    <div className="flex flex-col gap-6 max-md:-mx-6 max-md:gap-4">
      <SessionsGlance className={PHONE_EDGE} sessions={real} narrowed={!!f.q.trim() || !!f.projectId || !!f.machineId || !!f.agentKind || where.clauses.length > 0} />
      <Panel flush className={PHONE_EDGE}>
        <Toolbar counts={counts} agents={agents} views={(chips, clearScope, trailing) => <ViewBar entity="sessions" where={where} density={false} extraChips={chips} onClearExtra={clearScope} trailing={trailing} className="min-w-0 flex-1" />} />
        <OfflineBanner sessions={shown} />
        {groups.length === 0 ? (
          <EmptyState
            icon={<SearchX />}
            // One line: what was looked for, then the one way out.
            title={f.q.trim() ? `No sessions match "${f.q.trim()}"` : f.agentKind ? `No ${agentKindLabel(f.agentKind)} sessions match` : where.text ? `No session is ${where.text}` : 'No session has that status here'}
            action={
              isFiltered(f) || where.text ? (
                <Button
                  onClick={() => {
                    clear();
                    where.clear();
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div onKeyDown={onRowsKey} data-list="" className="pb-2 md:px-2">
            <ColumnHeader shared={shared} />
            {shown.length > VIRTUAL_MIN_ROWS ? (
              // Many sessions: only the rows near the viewport are mounted.
              <VirtualGroups
                groups={windowed}
                headers={headers}
                renderProject={(w) => {
                  const g = byKey.get(w.projectId)!;
                  return <VirtualStateHeader g={g} open={isOpen(g)} onToggle={() => setFolds((x) => ({ ...x, [g.key]: isOpen(g) }))} meta={groupMeta(g, now, since, blockedEnded)} />;
                }}
                // Windowed rows are placed one by one: each takes the group's indent step itself.
                renderSession={(s, style) => row(s, style, headers ? 'md:ml-6' : undefined)}
              />
            ) : headers ? (
              <div className="flex flex-col gap-2">
                {groups.map((g) => (
                  <SessionGroup
                    // Remount when the default fold changes (a search opens every group).
                    key={`${g.key}:${narrowed ? 'open' : 'rest'}:${folds[g.key] ?? ''}`}
                    g={g}
                    meta={groupMeta(g, now, since, blockedEnded)}
                    defaultCollapsed={!isOpen(g)}
                  >
                    <ul className="flex flex-col">
                      {g.folds
                        ? g.folds.map((fold) => (fold.runs.length > 1 ? <FailureFoldRow key={`fold:${fold.head.id}`} fold={fold} renderRun={(s) => row(s)} /> : row(fold.head)))
                        : g.sessions.map((s) => row(s))}
                    </ul>
                  </SessionGroup>
                ))}
              </div>
            ) : (
              <ul className="flex flex-col">
                {groups[0]!.folds
                  ? groups[0]!.folds.map((fold) => (fold.runs.length > 1 ? <FailureFoldRow key={`fold:${fold.head.id}`} fold={fold} renderRun={(s) => row(s)} /> : row(fold.head)))
                  : groups[0]!.sessions.map((s) => row(s))}
              </ul>
            )}
          </div>
        )}
      </Panel>
      <BulkBar
        count={sel.count}
        noun="session"
        total={shown.length}
        onSelectAll={sel.toggleAll}
        onClear={sel.clear}
        actions={[
          {
            id: 'stop',
            label: 'Stop',
            icon: <CircleStop />,
            danger: true,
            disabled: !running.length,
            hint: running.length ? 'Asks you to confirm by name. Policy still applies.' : 'None of these is running.',
            onClick: () => void performAct(stopSessionsAction(running, useOrgus.getState(), `bulk.stop:${running.length}`)),
          },
          {
            id: 'links',
            label: 'Copy links',
            icon: <Link2 />,
            onClick: () => {
              const text = picked.map((s) => `${window.location.origin}/sessions?session=${encodeURIComponent(s.id)}`).join('\n');
              void navigator.clipboard
                ?.writeText(text)
                .then(() => toast.success(`Copied ${plural(picked.length, 'link')}`))
                .catch(() => toast('Could not copy', { description: 'The browser blocked it; select the rows again and retry.' }));
            },
          },
        ]}
      />
    </div>
  );
});
